import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import type { Book } from '../types/database'
import { BookForm } from './BookForm'
import { Sidebar } from './Sidebar'
import { useAuth } from '../contexts/AuthContext'

type BookWithCount = Book & {
  question_groups: { count: number }[]
}

export function BookList() {
  const { roles } = useAuth()
  const navigate = useNavigate()
  const [books, setBooks] = useState<BookWithCount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editingBook, setEditingBook] = useState<Book | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const fetchBooks = async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('books')
        .select(`
          *,
          question_groups(count)
        `)
        .order('display_order', { ascending: true })

      if (!roles) {
        query = query.lt('status', 0)
      } else {
        query = query.gte('status', 0)
      }

      const { data, error } = await query

      if (error) throw error
      setBooks(data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch books')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBooks()
  }, [])

  const handleDelete = async (id: number) => {
    if (!confirm('정말 삭제하시겠습니까? 이 책에 속한 모든 문제 그룹도 함께 삭제됩니다.')) return

    try {
      const { error } = await supabase
        .from('books')
        .delete()
        .eq('id', id)

      if (error) throw error
      await fetchBooks()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete book')
    }
  }

  const handleEdit = (book: Book) => {
    setEditingBook(book)
    setShowForm(true)
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingBook(null)
    fetchBooks()
  }

  const handleViewGroups = (bookId: number) => {
    navigate(`/books/${bookId}/groups`)
  }

  const getGroupCount = (book: BookWithCount): number => {
    return book.question_groups?.[0]?.count || 0
  }

  const filteredBooks = books.filter((b) =>
    b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (b.description && b.description.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  if (loading) {
    return (
      <div className="relative flex min-h-screen w-full bg-background-light font-display">
        <Sidebar />
        <main className="ml-64 flex flex-1 items-center justify-center">
          <div className="flex items-center gap-2 text-gray-600">
            <span className="material-symbols-outlined animate-spin">progress_activity</span>
            <span className="text-lg">로딩 중...</span>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="relative flex min-h-screen w-full bg-background-light font-display">
      <Sidebar />
      <main className="ml-64 flex flex-1 flex-col p-6 lg:p-8">
        <div className="w-full max-w-7xl mx-auto">
          <header className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">교재 관리</h1>
            {roles.includes('moderator') && (
              <button
                onClick={() => setShowForm(true)}
                className="flex min-w-[84px] items-center justify-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold leading-normal tracking-wide hover:bg-primary/90 transition-colors"
              >
                <span className="material-symbols-outlined">add</span>
                <span className="truncate">새 교재 등록</span>
              </button>
            )}
          </header>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-base">error</span>
              <span className="text-sm">{error}</span>
            </div>
          )}

          {showForm && (
            <BookForm
              book={editingBook}
              onClose={handleFormClose}
            />
          )}

          <div className="mb-6 flex flex-wrap items-center gap-4">
            <div className="flex w-full flex-1 items-stretch rounded-lg h-10 bg-white border border-gray-200 focus-within:ring-2 focus-within:ring-primary lg:w-auto lg:flex-1">
              <div className="text-gray-400 flex items-center justify-center pl-3">
                <span className="material-symbols-outlined">search</span>
              </div>
              <input
                className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-r-lg text-gray-900 focus:outline-0 focus:ring-0 border-none bg-transparent h-full placeholder:text-gray-400 px-4 pl-2 text-sm"
                placeholder="교재 이름 또는 설명으로 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            {filteredBooks.length === 0 ? (
              <div className="text-center py-12">
                <span className="material-symbols-outlined text-gray-300 text-6xl mb-4">inbox</span>
                <p className="text-gray-500">
                  {searchQuery ? '검색 결과가 없습니다.' : '등록된 교재가 없습니다.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-max">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        순서
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        교재 이름
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        설명
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        문제 그룹 수
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        등록일
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        작업
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredBooks.map((book) => (
                      <tr key={book.id} className="hover:bg-gray-50 transition-colors">
                        <td className="h-[72px] px-4 py-2 text-sm text-gray-500">
                          {book.display_order}
                        </td>
                        <td className="h-[72px] px-4 py-2 text-sm font-medium text-gray-800">
                          {book.name}
                        </td>
                        <td className="h-[72px] px-4 py-2 text-sm text-gray-600">
                          {book.description || '-'}
                        </td>
                        <td className="h-[72px] px-4 py-2">
                          <button
                            onClick={() => handleViewGroups(book.id)}
                            className="inline-flex items-center gap-1 text-sm text-primary hover:underline cursor-pointer"
                          >
                            <span className="material-symbols-outlined text-base">folder</span>
                            <span>{getGroupCount(book)}개</span>
                          </button>
                        </td>
                        <td className="h-[72px] px-4 py-2 text-sm text-gray-500">
                          {new Date(book.created_at).toLocaleDateString('ko-KR', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </td>
                        <td className="h-[72px] px-4 py-2">
                          <div className="flex items-center gap-2">
                            {roles.includes('moderator') && (
                              <button
                                onClick={() => handleEdit(book)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-yellow-700 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
                              >
                                <span className="material-symbols-outlined text-sm">edit</span>
                                <span>수정</span>
                              </button>
                            )}
                            {roles.includes('moderator') && (
                              <button
                                onClick={() => handleDelete(book.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                              >
                                <span className="material-symbols-outlined text-sm">delete</span>
                                <span>삭제</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
